import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  IconButton,
  Alert,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { itemsApi, ItemStatus } from "../../services/api";
import compressImage from "../../utils/compressImage";

export default function ItemFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const isEdit = Boolean(id);

  const [description, setDescription] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [status, setStatus] = useState<ItemStatus>("purchased");
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [existingPhoto, setExistingPhoto] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && id) {
      itemsApi
        .get(Number(id))
        .then((res) => {
          const item = res.data;
          setDescription(item.description);
          setPurchasePrice(item.purchase_price);
          setSalePrice(item.sale_price || "");
          setStatus(item.status);
          setExistingPhoto(item.photo);
        })
        .catch(() => setError("Failed to load item."));
    }
  }, [id, isEdit]);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        setPhotoFile(compressed);
        const reader = new FileReader();
        reader.onload = () => setPhotoPreview(reader.result as string);
        reader.readAsDataURL(compressed);
      } catch {
        // Fallback to original file if compression fails
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onload = () => setPhotoPreview(reader.result as string);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("description", description);
      formData.append("purchase_price", purchasePrice);
      formData.append("status", status);

      if (salePrice) {
        formData.append("sale_price", salePrice);
      }
      if (photoFile) {
        formData.append("photo", photoFile);
      }

      if (isEdit && id) {
        await itemsApi.update(Number(id), formData);
        navigate(`/items/${id}`);
      } else {
        const res = await itemsApi.create(formData);
        navigate(`/items/${res.data.id}`);
      }
    } catch {
      setError("Failed to save item. Please check your inputs.");
    } finally {
      setSaving(false);
    }
  };

  const displayPhoto = photoPreview || existingPhoto;

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 0.5 }}>
        <IconButton onClick={() => navigate(isEdit ? `/items/${id}` : "/items")} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? "Edit Item" : "Add New Item"}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* Photo */}
          <Card
            sx={{
              overflow: "hidden",
              border: "2px dashed",
              borderColor: displayPhoto ? "transparent" : "grey.300",
              backgroundColor: "grey.50",
            }}
          >
            {displayPhoto ? (
              <Box
                component="img"
                src={displayPhoto}
                alt="Preview"
                sx={{
                  width: "100%",
                  height: { xs: 200, md: 320 },
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box
                sx={{
                  height: { xs: 120, md: 200 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 40 }} />
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1, p: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => cameraInputRef.current?.click()}
                fullWidth
                size="small"
              >
                Take Photo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryIcon />}
                onClick={() => uploadInputRef.current?.click()}
                fullWidth
                size="small"
              >
                Upload
              </Button>
            </Box>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={handlePhotoChange}
            />
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoChange}
            />
          </Card>

          {/* Form Fields */}
          <Card>
            <CardContent sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: { xs: 2, md: 3 },
              "&:last-child": { pb: { xs: 2, md: 3 } },
            }}>
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                required
                fullWidth
                placeholder="Describe the antique item..."
                size="medium"
              />

              <TextField
                label="Purchase Price"
                type="number"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                required
                fullWidth
                inputProps={{ inputMode: "decimal", pattern: "[0-9.]*" }}
                placeholder="0.00"
              />

              <TextField
                select
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value as ItemStatus)}
                fullWidth
              >
                <MenuItem value="purchased">Purchased</MenuItem>
                <MenuItem value="priced">Priced</MenuItem>
                <MenuItem value="in_booth">In Booth</MenuItem>
                <MenuItem value="sold">Sold</MenuItem>
              </TextField>

              {(status === "priced" || status === "in_booth" || status === "sold") && (
                <TextField
                  label="Sale Price"
                  type="number"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.01", min: "0", inputMode: "decimal" }}
                  placeholder="0.00"
                  helperText={status === "sold" ? "Enter the final sale price" : "Set the asking price"}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={saving || !description || !purchasePrice}
                fullWidth
              >
                {saving
                  ? "Saving..."
                  : isEdit
                  ? "Save Changes"
                  : "Add Item"}
              </Button>
            </CardContent>
          </Card>
        </Stack>
      </form>
    </Box>
  );
}
